import { useState } from "react";
import { Sparkles } from "lucide-react";
import { SpendDimensionsPanel } from "../components/dashboard/SpendDimensionsPanel";
import { TailFragmentationPanel } from "../components/dashboard/TailFragmentationPanel";
import { MaverickCompliancePanel } from "../components/dashboard/MaverickCompliancePanel";
import { SpendFilterSection } from "../components/dashboard/SpendFilterSection";

interface Props {
  onExecutiveDashboard?: () => void;
}

interface BarItem {
  label: string;
  value: string;
  pct: number;
}

const TABS = ["Overview", "Spend Dimensions", "Tail & Fragmentation", "Maverick & Compliance"];

const KPIS = [
  { label: "Total spend", value: "$48.2M", sub: "FY26 · 11,240 items", accent: "#6d28d9" },
  { label: "Avg transaction value", value: "$4.3K", sub: "spend ÷ 11,240 line items", accent: "#8b5cf6" },
  { label: "Suppliers", value: "312", sub: "normalized to parent", accent: "#a78bfa" },
  { label: "Classification coverage", value: "98%", sub: "UNSPSC L1–L4", accent: "#c4b5fd" },
  { label: "Data quality score", value: "91%", sub: "coverage, confidence, hygiene", accent: "#10b981" },
];

const HERO_STATS = [
  ["33%", "off-contract spend"],
  ["20.2%", "tail spend"],
  ["80%", "under control"],
];

const SOURCING: BarItem[] = [
  { label: "Catalog", value: "$14.9M", pct: 100 },
  { label: "Ad hoc", value: "$12.8M", pct: 86 },
  { label: "Panel", value: "$8.5M", pct: 57 },
  { label: "RFP", value: "$7.7M", pct: 52 },
  { label: "Contract", value: "$2.7M", pct: 18 },
  { label: "Single source", value: "$1.6M", pct: 11 },
];

const COST_CENTER: BarItem[] = [
  { label: "Operations", value: "65%", pct: 65 },
  { label: "R&D", value: "18%", pct: 18 },
  { label: "Facilities", value: "9%", pct: 9 },
  { label: "IT", value: "5%", pct: 5 },
  { label: "Admin", value: "3%", pct: 3 },
];

const GEOGRAPHY: BarItem[] = [
  { label: "India", value: "41.1%", pct: 41.1 },
  { label: "United States", value: "14.3%", pct: 14.3 },
  { label: "Canada", value: "12.3%", pct: 12.3 },
  { label: "Germany", value: "9.6%", pct: 9.6 },
  { label: "Mexico", value: "7.8%", pct: 7.8 },
  { label: "Japan", value: "6.6%", pct: 6.6 },
  { label: "United Kingdom", value: "5.1%", pct: 5.1 },
];

const CATEGORIES: BarItem[] = [
  { label: "Lubricants & engine oil", value: "$9.9M", pct: 100 },
  { label: "Chassis & body components", value: "$5.8M", pct: 59 },
  { label: "Braking systems", value: "$4.2M", pct: 42 },
  { label: "Electrical components", value: "$3.9M", pct: 39 },
  { label: "Fasteners & hardware", value: "$3.3M", pct: 33 },
  { label: "Bearings & power transmission", value: "$3M", pct: 30 },
  { label: "Tires & rubber", value: "$2.7M", pct: 27 },
  { label: "IT software & licenses", value: "$2.4M", pct: 24 },
  { label: "Castings & forgings", value: "$2.3M", pct: 23 },
  { label: "Filtration", value: "$1.9M", pct: 19 },
];

const PLANTS = [
  { color: "#1e1b4b", label: "Plant A · Detroit", pct: "47.5%" },
  { color: "#8b5cf6", label: "Plant B · Monterrey", pct: "27%" },
  { color: "#10b981", label: "Plant C · Pune", pct: "25.5%" },
];

const QUARTERS = [
  ["Q1 FY26", "$14.9M"],
  ["Q2 FY26", "$14.2M"],
  ["Q3 FY26", "$9.9M"],
  ["Q4 FY26", "$9.2M"],
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHLY_VALUES = [42, 48, 38, 35, 52, 44, 38, 32, 28, 30, 26, 24];

const HERO_BUTTONS = ["All", "What", "Where", "Who", "When"];

function BarRow({ label, value, pct }: BarItem) {
  return (
    <div className="si-bar-row">
      <p className="si-bar-row__label">{label}</p>
      <div className="si-bar-row__track">
        <div className="si-bar-row__fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="si-bar-row__value">{value}</p>
    </div>
  );
}

function BarList({ items, className = "" }: { items: BarItem[]; className?: string }) {
  return (
    <div className={`si-bar-list ${className}`.trim()}>
      {items.map((item) => (
        <BarRow key={item.label} {...item} />
      ))}
    </div>
  );
}

function DonutChart() {
  const cx = 70;
  const cy = 70;
  const r = 54;
  const strokeW = 18;
  const circ = 2 * Math.PI * r;
  const segments = [
    { pct: 47.5, color: "#1e1b4b" },
    { pct: 27, color: "#8b5cf6" },
    { pct: 25.5, color: "#10b981" },
  ];
  let offset = 0;

  return (
    <svg className="si-donut" width="140" height="140" viewBox="0 0 140 140">
      {segments.map((seg, i) => {
        const dash = (seg.pct / 100) * circ;
        const rotation = (offset / 100) * 360 - 90;
        offset += seg.pct;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeW}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeLinecap="butt"
            transform={`rotate(${rotation} ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" fill="#99a1af" fontSize="10">Total</text>
      <text x={cx} y={cy + 9} textAnchor="middle" fill="#1e2939" fontSize="14" fontWeight="700">$48.2M</text>
    </svg>
  );
}

function SpendLineChart() {
  const W = 900;
  const H = 100;
  const pad = { t: 8, b: 0, l: 4, r: 4 };
  const xStep = (W - pad.l - pad.r) / (MONTHLY_VALUES.length - 1);
  const maxV = Math.max(...MONTHLY_VALUES);
  const pts = MONTHLY_VALUES.map(
    (v, i) => [pad.l + i * xStep, H - pad.b - (v / maxV) * (H - pad.t - pad.b)] as [number, number],
  );
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
  const areaD = `${pathD} L${pts[pts.length - 1][0]},${H} L${pts[0][0]},${H} Z`;

  return (
    <div className="si-line-chart">
      <svg className="si-line-chart__svg" width="100%" viewBox={`0 0 ${W} ${H + 20}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="siLineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(102,89,242,0.25)" />
            <stop offset="100%" stopColor="rgba(102,89,242,0)" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#siLineGrad)" />
        <path d={pathD} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#8b5cf6" />
        ))}
      </svg>
      <div className="si-line-chart__months">
        {MONTHS.map((m) => (
          <p key={m} className="si-line-chart__month">{m}</p>
        ))}
      </div>
    </div>
  );
}

export function SpendIntelligenceView(_props: Props) {
  const [activeTab, setActiveTab] = useState("Overview");

  return (
    <div className="si-page">
      <div className="si-header">
        <p className="si-header__eyebrow">Spend</p>
        <h2 className="si-header__title">Spend</h2>
        <p className="si-header__description">
          Continuously generated recommendations across consolidation, leakage, tail spend, and payment optimization.
        </p>
      </div>

      <section className="si-hero">
        <p className="si-hero__headline">
          Two thirds of your spend is under control. The other third, <span className="si-hero__accent">$15.9M</span>,
          leaks through off-contract and tail buying.
        </p>
        <p className="si-hero__sub">
          Built cleanly, completely, plants and externally from the Evaluserve Spend dataset.
        </p>
        <div className="si-hero__buttons">
          {HERO_BUTTONS.map((btn, i) => (
            <button
              key={btn}
              type="button"
              className={`si-hero__chip${i === 0 ? " si-hero__chip--active" : ""}`}
            >
              {btn}
            </button>
          ))}
        </div>
        <div className="si-hero__footer">
          <div className="si-hero__stats">
            {HERO_STATS.map(([val, sub]) => (
              <div key={sub} className="si-hero__stat">
                <p className="si-hero__stat-value">{val}</p>
                <p className="si-hero__stat-label">{sub}</p>
              </div>
            ))}
          </div>
          <div className="si-hero__cta-group">
            <button type="button" className="si-hero__cta">Tighten contract compliance →</button>
            <p className="si-hero__note">Only single-source spend data</p>
          </div>
        </div>
        <div className="si-hero__synth">
          <Sparkles size={11} />
          <p className="si-hero__synth-text">AI synthesis · updated 4 min ago</p>
        </div>
      </section>

      <SpendFilterSection />

      <section className="si-deep-dive">
        <p className="si-section-eyebrow">Deep Dive by Section</p>
        <div className="si-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`si-tab${activeTab === t ? " si-tab--active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>
        {activeTab === "Overview" && (
          <div className="si-kpi-grid">
            {KPIS.map(({ label, value, sub, accent }) => (
              <div key={label} className="si-kpi" style={{ borderLeftColor: accent, borderBottomColor: accent }}>
                <p className="si-kpi__label">{label}</p>
                <p className="si-kpi__value">{value}</p>
                <p className="si-kpi__sub">{sub}</p>
              </div>
            ))}
          </div>
        )}
        {activeTab === "Spend Dimensions" && <SpendDimensionsPanel />}
        {activeTab === "Tail & Fragmentation" && <TailFragmentationPanel />}
        {activeTab === "Maverick & Compliance" && <MaverickCompliancePanel />}
      </section>

      {activeTab === "Overview" && (
      <>
      <div className="si-grid-2">
        <div className="si-card">
          <p className="si-card__title si-card__title--spaced">Spend by Plant</p>
          <div className="si-plant">
            <DonutChart />
            <div className="si-plant__legend">
              {PLANTS.map(({ color, label, pct }) => (
                <div key={label} className="si-plant__item">
                  <div className="si-plant__swatch" style={{ background: color }} />
                  <p className="si-plant__label">{label}</p>
                  <p className="si-plant__pct">{pct}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="si-card">
          <p className="si-card__title si-card__title--spaced">Spend by Sourcing Channel</p>
          <BarList items={SOURCING} />
        </div>
      </div>

      <div className="si-card">
        <div className="si-card__head">
          <div>
            <p className="si-card__title">Spend over Time, Monthly</p>
            <p className="si-card__subtitle si-card__subtitle--accent">Q1-Q2 carries 60% of the year</p>
          </div>
          <span className="si-badge">FRONT-LOADED</span>
        </div>
        <SpendLineChart />
        <div className="si-quarters">
          {QUARTERS.map(([q, v]) => (
            <div key={q} className="si-quarters__item">
              <p className="si-quarters__label">{q}</p>
              <p className="si-quarters__value">{v}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="si-grid-2">
        <div className="si-card">
          <p className="si-card__title si-card__title--spaced">Spend by Cost Center</p>
          <BarList items={COST_CENTER} />
        </div>
        <div className="si-card">
          <p className="si-card__title si-card__title--spaced">Spend by Geography</p>
          <BarList items={GEOGRAPHY} />
        </div>
      </div>

      <div className="si-card si-card--last">
        <div className="si-card__head">
          <div>
            <p className="si-card__title">Top Categories</p>
            <p className="si-card__subtitle">L3-level view across 240+ categories · FY26, validated</p>
          </div>
          <button type="button" className="si-link-btn">CLICK TO DRILL</button>
        </div>
        <BarList items={CATEGORIES} className="si-bar-list--tight" />
        <p className="si-card__footnote">
          Lubricants (88.1M, 18.4%) lead spend. The maverick and tail detail survive the headline line in the Maverick
          &amp; Compliance and Tail &amp; Fragmentation tabs.
        </p>
      </div>
      </>
      )}
    </div>
  );
}
